export interface CartItem {
  id: string
  product_id?: string
  title: string
  price: number
  quantity: number
  image: string
  size?: string
  color?: string
  variant?: string
  type?: string
}

/** Total unit count across all items */
export function calcTotalItems(items: CartItem[]): number {
  return items.reduce((total, item) => total + item.quantity, 0)
}

/** Total price (before formatting) */
export function calcTotalPrice(items: CartItem[]): number {
  return items.reduce((total, item) => total + item.price * item.quantity, 0)
}

/** Returns the index of an existing item that matches product_id+size+color+type, or -1 */
export function findExistingItemIndex(items: CartItem[], item: CartItem): number {
  return items.findIndex(
    (e) =>
      e.product_id === item.product_id &&
      e.size === item.size &&
      e.color === item.color &&
      e.type === item.type
  )
}

/** Validation before adding an item */
export function validateCartItem(item: CartItem | null | undefined): string | null {
  if (!item) return 'item is null or undefined'
  if (!item.size) return 'size is missing'
  if (!item.product_id) return 'product_id is missing'
  return null
}

/** Sort Supabase cart rows newest-first (by created_at DESC then id DESC) */
export function sortSupabaseCartRows(rows: any[]): any[] {
  return [...rows].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime()
    const dateB = new Date(b.created_at).getTime()
    if (dateB !== dateA) return dateB - dateA
    const idA = typeof a.id === 'number' ? a.id : parseInt(a.id, 10)
    const idB = typeof b.id === 'number' ? b.id : parseInt(b.id, 10)
    return idB - idA
  })
}

/** Merge incoming items into existing, preserving local order and prepending new items */
export function mergeCartItems(existing: CartItem[], incoming: CartItem[]): CartItem[] {
  const merged: CartItem[] = []
  const processedKeys = new Set<string>()
  const key = (i: CartItem) => `${i.product_id}-${i.size}-${i.color}-${i.type}`

  for (const existingItem of existing) {
    const k = key(existingItem)
    const newItem = incoming.find((n) => key(n) === k)
    if (newItem) {
      merged.push({ ...existingItem, ...newItem })
      processedKeys.add(k)
    } else {
      merged.push(existingItem)
    }
  }

  for (const newItem of incoming) {
    if (!processedKeys.has(key(newItem))) {
      merged.unshift(newItem)
    }
  }

  return merged
}

/** Clamp item quantities to available stock (0-stock items are kept, not removed) */
export function applyStockConstraints(
  items: CartItem[],
  inventory: { product_sku: string; quantity: number }[]
): { items: CartItem[]; hasChanges: boolean } {
  let hasChanges = false
  const result = items.map((item) => {
    const stockItem = inventory.find(
      (inv) =>
        String(inv.product_sku).toLowerCase() === String(item.product_id || '').toLowerCase()
    )
    const availableStock = stockItem ? stockItem.quantity : 0
    if (item.quantity > availableStock && availableStock > 0) {
      hasChanges = true
      return { ...item, quantity: availableStock }
    }
    return item
  })
  return { items: result, hasChanges }
}
